import { useState, useMemo } from 'react';
import { FilterOption } from '@/components/ui/FilterDropdown';
import { PLACEHOLDER_CONTACTS } from '@/hooks/placeholder-data/placeholderData';
import { useColumnConfig } from '@/hooks/column-config';
import { toast } from 'sonner';

// Define types locally
interface Contact {
  id?: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  company?: string;
  industry?: string;
  status?: string;
  extra_fields?: {
    industry?: string;
    status?: string;
    notes?: string;
  };
  crm_companies?: {
    name?: string;
    extra_fields?: {
      industry?: string;
    };
  };
}

// Default filter options
const defaultContactFilters: FilterOption[] = [
  { id: 'technology', label: 'Technology', checked: false },
  { id: 'healthcare', label: 'Healthcare', checked: false },
  { id: 'finance', label: 'Finance', checked: false },
  { id: 'retail', label: 'Retail', checked: false },
];

// Filter function
const filterContactsByIndustry = (contacts: Contact[], filterOptions: FilterOption[]): Contact[] => {
  const activeFilters = filterOptions.filter(option => option.checked);
  if (activeFilters.length === 0) return contacts;
  
  return contacts.filter(contact => {
    const industry = contact.extra_fields?.industry || contact.industry || '';
    return activeFilters.some(filter => 
      industry.toLowerCase().includes(filter.id.toLowerCase())
    );
  });
};

// Get table columns
const getContactTableColumns = (columnConfig: any[]) => {
  return columnConfig.map(column => ({
    ...column,
    options: {
      render: (value: any) => value
    }
  }));
};

const useContactsPage = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isColumnModalOpen, setIsColumnModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [filterOptions, setFilterOptions] = useState<FilterOption[]>(defaultContactFilters);
  
  const contacts = PLACEHOLDER_CONTACTS;
  const isLoading = false;
  
  const { columnConfig, isAdmin, updateColumnConfig } = useColumnConfig('contacts');

  const handleRowClick = (contact: Contact) => {
    setSelectedContact(contact);
    setIsEditModalOpen(true);
  };
  
  const handleFilterChange = (updatedOptions: FilterOption[]) => {
    setFilterOptions(updatedOptions);
  };
  
  const openAddModal = () => setIsAddModalOpen(true);
  const closeAddModal = () => setIsAddModalOpen(false);
  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedContact(null);
  };
  const closeColumnModal = () => setIsColumnModalOpen(false);
  
  const filteredContacts = useMemo(() => 
    filterContactsByIndustry(contacts || [], filterOptions), 
    [contacts, filterOptions]
  );
  
  const columns = getContactTableColumns(columnConfig);
  
  return {
    contacts: filteredContacts || [],
    isLoading,
    isAdmin,
    columnConfig,
    isAddModalOpen,
    isEditModalOpen,
    isColumnModalOpen,
    selectedContact,
    filterOptions,
    handleRowClick,
    handleFilterChange,
    openAddModal,
    closeAddModal,
    closeEditModal,
    closeColumnModal,
    updateColumnConfig,
    columns
  };
};

export default useContactsPage;
